import React, {useEffect, useState} from 'react';
import {addMonths, format, lastDayOfMonth, setMonth, setYear, subMonths} from 'date-fns';
import {ChevronLeftIcon, ChevronRightIcon} from '@heroicons/react/24/solid';
import {useDatePickerStore} from './DatepickerStore';

const DatePicker = () => {
    const {startDate, setStartDate, endDate, setEndDate} = useDatePickerStore();

    // Synchronisiere die Start- und Enddaten mit den Initialwerten aus dem Store
    const [currentStartDate, setCurrentStartDate] = useState(startDate);
    const [currentEndDate, setCurrentEndDate] = useState(endDate);

    const [showStartDateSelector, setShowStartDateSelector] = useState(false);
    const [showEndDateSelector, setShowEndDateSelector] = useState(false);
    const [selectedYear, setSelectedYear] = useState(currentStartDate.getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(currentStartDate.getMonth());
    const [selectedEndYear, setSelectedEndYear] = useState(currentEndDate.getFullYear());
    const [selectedEndMonth, setSelectedEndMonth] = useState(currentEndDate.getMonth());

    useEffect(() => {
        setCurrentStartDate(startDate);
        setCurrentEndDate(endDate);
        setSelectedYear(startDate.getFullYear());
        setSelectedMonth(startDate.getMonth());
        setSelectedEndYear(endDate.getFullYear());
        setSelectedEndMonth(endDate.getMonth());
    }, [startDate, endDate]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.date-selector')) {
                setShowStartDateSelector(false);
                setShowEndDateSelector(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Format für den ersten und letzten Tag des Monats
    const firstDateOfMonth = format(currentStartDate, 'yyyy-MM-dd'); // Update this to use the correct date
    const lastDateOfMonth = format(lastDayOfMonth(currentEndDate), 'yyyy-MM-dd');

    // Funktion zum Wechseln des Startdatums nach links (vorheriger Monat)
    const handlePreviousStartMonth = () => {
        const newStartDate = subMonths(currentStartDate, 1);
        setCurrentStartDate(newStartDate); // Update local state
        setStartDate(newStartDate); // Update store
    };

    // Funktion zum Wechseln des Startdatums nach rechts (nächster Monat)
    const handleNextStartMonth = () => {
        const newStartDate = addMonths(currentStartDate, 1);
        setCurrentStartDate(newStartDate); // Update local state
        setStartDate(newStartDate); // Update store
    };

    // Funktion zum Wechseln des Enddatums nach links (vorheriger Monat)
    const handlePreviousEndMonth = () => {
        let newEndDate = subMonths(currentEndDate, 1);
        newEndDate = lastDayOfMonth(newEndDate);
        setCurrentEndDate(newEndDate); // Update local state
        setEndDate(newEndDate); // Update store
    };

    // Funktion zum Wechseln des Enddatums nach rechts (nächster Monat)
    const handleNextEndMonth = () => {
        let newEndDate = addMonths(currentEndDate, 1);
        newEndDate = lastDayOfMonth(newEndDate);
        setCurrentEndDate(newEndDate); // Update local state
        setEndDate(newEndDate); // Update store
    };

    // Funktion zum Umschalten der Anzeige des Startdatumauswahl-Divs
    const toggleStartDateSelector = () => {
        setShowStartDateSelector((prevShow) => !prevShow);
        setShowEndDateSelector(false);
    };

    // Funktion zum Umschalten der Anzeige des Enddatumauswahl-Divs
    const toggleEndDateSelector = () => {
        setShowEndDateSelector((prevShow) => !prevShow);
        setShowStartDateSelector(false);
    };

    // Funktion zum Festlegen des Jahres und Monats für das Startdatum
    const handleYearChange = (event) => {
        const newYear = parseInt(event.target.value, 10);
        setSelectedYear(newYear);
        const newDate = setYear(currentStartDate, newYear);
        setCurrentStartDate(newDate);
        setStartDate(newDate);
    };

    const handleMonthChange = (event) => {
        const newMonth = parseInt(event.target.value, 10);
        setSelectedMonth(newMonth);
        const newDate = setMonth(currentStartDate, newMonth);
        setCurrentStartDate(newDate);
        setStartDate(newDate);
    };

    // Funktion zum Festlegen des Jahres und Monats für das Enddatum
    const handleEndYearChange = (event) => {
        const newYear = parseInt(event.target.value, 10);
        setSelectedEndYear(newYear);
        const newEndDate = setYear(currentEndDate, newYear);
        setCurrentEndDate(newEndDate);
        setEndDate(newEndDate);
    };

    const handleEndMonthChange = (event) => {
        const newMonth = parseInt(event.target.value, 10);
        setSelectedEndMonth(newMonth);
        const newEndDate = setMonth(currentEndDate, newMonth);
        setCurrentEndDate(newEndDate);
        setEndDate(newEndDate);
    };

    return (
        <main className='flex items-end rounded-lg relative'>
            {/* Startdatum: Button zum Wechseln zum vorherigen Monat */}
            <button
                onClick={handlePreviousStartMonth}
                className='px-2 py-1 bg-color-5 rounded-tl-full rounded-bl-full flex items-center shadow-sm border border-color-2'
            >
                <div className='w-5 h-5 py-1 font-color-1 flex items-center'>
                    <ChevronLeftIcon/>
                </div>
            </button>

            {/* Anzeige des Startdatums */}
            <button
                onClick={toggleStartDateSelector}
                className='w-28 px-2 py-1 bg-color-5 font-color-1 text-sm flex items-center justify-center shadow-sm border border-color-2'
            >
                {firstDateOfMonth}
            </button>

            {/* Startdatum: Button zum Wechseln zum nächsten Monat */}
            <button
                onClick={handleNextStartMonth}
                className='px-2 py-1 bg-color-5 rounded-tr-full rounded-br-full flex items-center shadow-sm border border-color-2'
            >
                <div className='w-5 h-5 py-1 font-color-1 flex items-center'>
                    <ChevronRightIcon/>
                </div>
            </button>

            {/* Enddatum: Button zum Wechseln zum vorherigen Monat */}
            <button
                onClick={handlePreviousEndMonth}
                className='px-2 py-1 bg-color-5 rounded-tl-full rounded-bl-full flex items-center shadow-sm border border-color-2 ml-4'
            >
                <div className='w-5 h-5 py-1 font-color-1 flex items-center'>
                    <ChevronLeftIcon/>
                </div>
            </button>

            {/* Anzeige des Enddatums */}
            <button
                onClick={toggleEndDateSelector}
                className='w-28 px-2 py-1 bg-color-5 font-color-1 text-sm flex items-center justify-center shadow-sm border border-color-2'
            >
                {lastDateOfMonth}
            </button>

            {/* Enddatum: Button zum Wechseln zum nächsten Monat */}
            <button
                onClick={handleNextEndMonth}
                className='px-2 py-1 bg-color-5 rounded-tr-full rounded-br-full flex items-center shadow-sm border border-color-2'
            >
                <div className='w-5 h-5 py-1 font-color-1 flex items-center'>
                    <ChevronRightIcon/>
                </div>
            </button>

            {/* Eingeblendetes Div zur Auswahl des Startdatums */}
            {showStartDateSelector && (
                <div
                    className='date-selector absolute top-full mt-2 bg-white border border-color-2 rounded-lg shadow-lg p-4 z-10'>
                    <div className='flex justify-between mb-4'>
                        {/* Jahr Auswahl */}
                        <div>
                            <label htmlFor='year' className='block text-sm font-medium text-gray-700'>Jahr</label>
                            <select
                                id='year'
                                value={selectedYear}
                                onChange={handleYearChange}
                                className='mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none sm:text-sm'
                            >
                                {Array.from({length: 10}, (_, i) => selectedYear - 5 + i).map((year) => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                        </div>
                        {/* Monat Auswahl */}
                        <div>
                            <label htmlFor='month' className='block text-sm font-medium text-gray-700'>Monat</label>
                            <select
                                id='month'
                                value={selectedMonth}
                                onChange={handleMonthChange}
                                className='mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none sm:text-sm'
                            >
                                {Array.from({length: 12}, (_, i) => (
                                    <option key={i} value={i}>{format(setMonth(new Date(), i), 'MMMM')}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            )}

            {/* Eingeblendetes Div zur Auswahl des Enddatums */}
            {showEndDateSelector && (
                <div
                    className='date-selector absolute top-full mt-2 bg-white border border-color-2 rounded-lg shadow-lg p-4 z-10'>
                    <div className='flex justify-between mb-4'>
                        {/* Jahr Auswahl */}
                        <div>
                            <label htmlFor='endYear' className='block text-sm font-medium text-gray-700'>Jahr</label>
                            <select
                                id='endYear'
                                value={selectedEndYear}
                                onChange={handleEndYearChange}
                                className='mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none sm:text-sm'
                            >
                                {Array.from({length: 10}, (_, i) => selectedEndYear - 5 + i).map((year) => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                        </div>
                        {/* Monat Auswahl */}
                        <div>
                            <label htmlFor='endMonth' className='block text-sm font-medium text-gray-700'>Monat</label>
                            <select
                                id='endMonth'
                                value={selectedEndMonth}
                                onChange={handleEndMonthChange}
                                className='mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none sm:text-sm'
                            >
                                {Array.from({length: 12}, (_, i) => (
                                    <option key={i} value={i}>{format(setMonth(new Date(), i), 'MMMM')}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
};

export default DatePicker;
